/**
 * THE SHELL-SIDE HALF OF THE META ROUTER — and the only file under
 * `engine/meta/` that `main.ts` is allowed to import statically.
 *
 * ── WHY THIS FILE EXISTS AT ALL ─────────────────────────────────────────────
 * `main.ts` is the entry. Everything statically reachable from it is downloaded
 * before a visitor sees anything and is charged to the 150 KB gz shell budget
 * (ADR-0005), which is currently sitting at ~105 KB. The seven §6 meta screens
 * — Battle, Cards, Shop, House, Events, Profile, Settings — plus the crate
 * slots, the modes hub and the friends list are META, not shell: a signed-out
 * visitor reading the terms of service must not pay for the deck builder.
 *
 * So the router needs to answer ONE question synchronously — "is this path a
 * meta screen?" — and everything else lazily. That question is answered here,
 * from a frozen table of strings with no DOM, no i18n, no HTTP and no imports.
 * `mountMetaRoute()` is the async hop: it returns a teardown IMMEDIATELY (the
 * router's contract is synchronous) and fetches `./shell` behind it.
 *
 * Adding a screen means adding a line to {@link META_PATHS} and a case to
 * `screens.ts`. It does NOT mean touching `main.ts`.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 * The tab order, the icons, the i18n keys and the navigation EDGE GRAPH live in
 * `./nav.ts`, which is meta-chunk code. Only the path table is shell.
 */
import { t } from '@/engine/i18n';
import { createButton } from '@/engine/ui/button';
import { createSkeleton } from '@/engine/ui/feedback';


/** The screens the meta shell mounts. `battle` is the home. */
export type MetaScreenId =
  | 'battle'
  | 'cards'
  | 'shop'
  | 'house'
  | 'events'
  | 'profile'
  | 'settings'
  | 'crates'
  | 'modes'
  | 'social'
  | 'leaderboard'
  | 'wallet'
  | 'world'
  | 'wardrobe';

/**
 * Screen -> canonical path.
 *
 * `/play` is the home and stays `/play` on purpose: it is the path the landing
 * page's call to action points at, the path every sibling module's docblock
 * already spells in its `onClose: () => navigate('/play')` example, and the
 * path `engine/modes/invite-link.ts` mints share links under. Renaming it would
 * silently break all three.
 */
export const META_PATHS: Readonly<Record<MetaScreenId, string>> = Object.freeze({
  battle: '/play',
  cards: '/cards',
  shop: '/shop',
  house: '/house',
  events: '/events',
  profile: '/profile',
  settings: '/settings',
  crates: '/crates',
  modes: '/modes',
  social: '/social',
  leaderboard: '/leaderboard',
  wallet: '/wallet',
  world: '/world',
  wardrobe: '/wardrobe',
});

/**
 * THE FULL-VIEWPORT MATCH SURFACE. Not a meta screen: no tab bar, no site
 * chrome, `100dvh` to itself (`engine/bootstrap.ts` sizes the stage on that
 * assumption). It lives here rather than in `nav.ts` because `layout/routes.ts`
 * — shell code — has to know it is an application surface, and `nav.ts` is meta.
 */
export const ARENA_PATH = '/arena';

/**
 * THE PUBLISHED CRATE DROP TABLES, and the one path in this file that KEEPS the
 * site chrome. §4 requires the odds to be readable in-app; the endpoint behind
 * it is `@Public()` precisely so a player deciding whether to spend, or a
 * regulator, can read them without an account — which makes it a DOCUMENT, and
 * documents get the header, the footer and the language control.
 */
export const ODDS_PATH = '/crates/odds';

/** Every meta screen id, in nav order. Frozen; `nav.ts` orders the tabs. */
export const META_SCREEN_IDS: readonly MetaScreenId[] = Object.freeze(
  Object.keys(META_PATHS) as MetaScreenId[],
);

/** The home screen. Every §6 screen must be reachable from here in <= 2 taps. */
export const META_HOME: MetaScreenId = 'battle';

const BY_PATH: ReadonlyMap<string, MetaScreenId> = new Map(
  (Object.entries(META_PATHS) as [MetaScreenId, string][]).map(([id, path]) => [path, id]),
);

/**
 * `/play/ABCD2345` — a Friendly or Tournament invite link, minted by
 * `engine/modes/invite-link.ts` (`INVITE_ROUTE = '/play'`). It opens the modes
 * hub with the code already filled in, which is the entire point of sharing
 * one. The pattern is deliberately loose (the screen validates the code, not
 * the router) but bounded, so `/play/` and a 400-character path both miss.
 */
const INVITE_PATH = /^\/play\/[A-Za-z0-9-]{1,32}$/;

/** `/faq/` and `/faq` are the same page. Mirrors `layout/routes.ts`. */
function normalise(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.replace(/\/+$/, '');
  return path === '' ? '/' : path;
}

/**
 * Which meta screen owns `path`, or `null`.
 *
 * TOTAL and synchronous — the router calls it on every navigation before it has
 * loaded anything, and a `null` here is what makes an unknown path fall through
 * to the real 404 rather than to a blank meta shell.
 */
export function metaScreenForPath(path: string): MetaScreenId | null {
  const normalised = normalise(path);
  const direct = BY_PATH.get(normalised);
  if (direct !== undefined) return direct;
  if (INVITE_PATH.test(normalised)) return 'modes';
  return null;
}

/** Convenience predicate. `metaScreenForPath(path) !== null`. */
export function isMetaPath(path: string): boolean {
  return metaScreenForPath(path) !== null;
}

/** What `mountMetaRoute` needs from the router. Nothing else is passed. */
export interface MetaRouteOptions {
  /** The routed application root — the same `#app` every screen owns. */
  readonly root: HTMLElement;
  /** The full path the player arrived on, invite code and all. */
  readonly path: string;
  /**
   * `location.search`, including the leading `?`, or `''`.
   *
   * It exists for ONE consumer: an invite may arrive as `/play/<code>` or as
   * `?invite=<code>` (`engine/modes/invite-link.ts` accepts both), and the
   * modes screen is handed `path + search` so it can read either. The router
   * must not strip it — a shared link that loses its query is a dead link.
   */
  readonly search?: string;
  /** Push a new path. The shell's nav and every screen's links go through it. */
  navigate(to: string): void;
  /** End the session. Wired to the Settings screen's sign-out. */
  onSignOut?(): void;
  /**
   * The session is over because the ACCOUNT is — a password change revoked
   * every session, or the account was deleted.
   *
   * NOT the same thing as `onSignOut`, and the difference is the reason it is a
   * second callback rather than a reuse of the first: `onSignOut` navigates
   * away, and the deletion farewell (`account-screen.ts:deletedView`) is the
   * only confirmation a player ever gets that their account is gone. Navigating
   * on top of it would delete an account and then show the player a lobby.
   *
   * `main.ts` supplies it (disconnect the socket, clear the auth store, do NOT
   * navigate). Absent, `renderAccountScreen` falls back to clearing the auth
   * store itself, which is correct but leaves the socket up.
   */
  onSessionEnded?(): void;
  /**
   * Test seam. Injected so `test/meta-screens.test.ts` can drive the shell
   * without a dynamic import resolving on a later microtask.
   */
  readonly load?: () => Promise<{
    mountMetaShell(options: MetaShellMountOptions): () => void;
  }>;
  /** Passed straight through to the shell. See {@link ScreenMounter}. */
  readonly mount?: ScreenMounter;
}

/** What `./shell` is handed. Kept here so this file needs no import from it. */
export interface MetaShellMountOptions {
  readonly root: HTMLElement;
  readonly screen: MetaScreenId;
  readonly path: string;
  readonly search?: string;
  navigate(to: string): void;
  onSignOut?(): void;
  /** See {@link MetaRouteOptions.onSessionEnded}. Passed through to the screen. */
  onSessionEnded?(): void;
  /** Test seam — see {@link ScreenMounter}. */
  readonly mount?: ScreenMounter;
}

/**
 * EVERYTHING A SCREEN IS GIVEN. There is nothing else, and that is the point.
 *
 * A screen gets a host element it owns outright, the id it was mounted as, the
 * URL it was reached by, and one function to leave. It does NOT get the shell,
 * the header, the tab bar, the api client or the session — a screen that could
 * reach into the chrome would be a screen the chrome could not rebuild, and
 * rebuilding the chrome on every navigation is what makes "no listener
 * survives a tab change" true by construction rather than by review.
 */
export interface MetaScreenContext {
  /** A fresh element inside the shell's `<main>`. The screen owns it whole. */
  readonly host: HTMLElement;
  readonly screen: MetaScreenId;
  /** The path the player arrived on, plus `search`. Invite codes live here. */
  readonly path: string;
  navigate(to: string): void;
  onSignOut?(): void;
  /**
   * See {@link MetaRouteOptions.onSessionEnded}. Only the Settings screen reads
   * it, because Settings is the only screen that can end an account now that
   * `/account` is a section of it rather than a page of its own (IA §4).
   */
  onSessionEnded?(): void;
}

/**
 * THE MOUNT POINT every other Phase 5B screen plugs into.
 *
 * `screens.ts` is the shipped implementation: one `await import()` per screen,
 * so the Shop's code is fetched when the Shop is opened and never before. It is
 * injectable so `test/meta-screens.test.ts` can drive the shell's LIFECYCLE —
 * mount, teardown, navigation — against stub screens it controls, rather than
 * against ten other agents' modules and their HTTP.
 *
 * The contract is exactly two lines long, and both matter:
 *   · resolve with a teardown that leaves `ctx.host` as it found it;
 *   · reject only for a genuinely unloadable screen — the shell renders the
 *     failure notice, it does not swallow it.
 */
export type ScreenMounter = (ctx: MetaScreenContext) => Promise<() => void>;

/**
 * Mount the meta shell for `path`, lazily, and return its teardown NOW.
 *
 * ── THE GENERATION GUARD ────────────────────────────────────────────────────
 * The import can resolve after the player has already navigated somewhere else
 * — a slow network and a fast thumb are not a rare combination on a phone. The
 * returned teardown flips `live`, so a resolution that loses the race mounts
 * nothing instead of painting the Shop on top of the match. This is the same
 * discipline `engine/bootstrap.ts` uses for `./match`, and it is here for the
 * same reason: Phase 2 shipped a leaked screen exactly once.
 */
export function mountMetaRoute(options: MetaRouteOptions): () => void {
  const screen = metaScreenForPath(options.path);
  if (screen === null) {
    /*
     * UNREACHABLE BY CONSTRUCTION, AND LOUD ANYWAY. Both callers in `main.ts`
     * hand over a path that has a screen — one inside the `isMetaPath(path)`
     * branch, one hard-coded to `ROUTES.play` by `mountArenaInShell`. But the
     * old `return () => undefined` turned any future mistake into a COMPLETELY
     * BLANK PAGE with no console output and a green test suite: changing
     * `mountArenaInShell`'s path to `ROUTES.arena` (which has no meta screen)
     * blanked /arena and all 2609 tests still passed. A blank page is the worst
     * symptom a router can have, because it looks like a load that never
     * finished. Say so instead — and still return a real teardown, since
     * throwing here would take the whole render with it.
     */
    console.error(
      `mountMetaRoute: no meta screen is registered for "${options.path}" — ` +
        'nothing will render. This is a routing bug, not a missing chunk.',
    );
    return () => undefined;
  }

  let live = true;
  let inner: (() => void) | null = null;

  /*
   * CLEAR THE OUTGOING SCREEN NOW. Same fault, same fix as `mountLazy`: the
   * router's teardown only promises to stop a screen being interactive, and
   * core's login screen honours exactly that — it writes `root.innerHTML` and
   * returns a teardown that removes one listener. Without this, signing in left
   * the sign-in form on the page for the whole of the meta chunk's download,
   * and the game mounted around it.
   */
  const showLoading = (): void => {
    const p = options.root.ownerDocument.createElement('p');
    p.className = 'p-6 text-sm text-text-dim';
    p.dataset.testid = 'meta-loading';
    p.textContent = t('meta.loading');
    // A shimmer under the line, so a slow chunk on 4G reads as "arriving"
    // rather than as a page that stopped. The line stays: it is what a screen
    // reader hears, and the skeleton is aria-hidden.
    const skeleton = createSkeleton(['title', 'line', 'line', 'card', 'card']);
    skeleton.classList.add('px-6');
    options.root.replaceChildren(p, skeleton);
  };

  const load = options.load ?? (() => import('./shell'));

  const attempt = (): void => {
    showLoading();
    void load().then(
      (mod) => {
        if (!live) return;
        // Same as `mountLazy`: clear the placeholder before the shell mounts, or
        // a shell that appends leaves it as a sibling and inflates `#app`.
        options.root.replaceChildren();
        inner = mod.mountMetaShell({
          root: options.root,
          screen,
          path: options.path,
          search: options.search ?? '',
          navigate: options.navigate,
          ...(options.onSignOut === undefined ? {} : { onSignOut: options.onSignOut }),
          ...(options.onSessionEnded === undefined
            ? {}
            : { onSessionEnded: options.onSessionEnded }),
          ...(options.mount === undefined ? {} : { mount: options.mount }),
        });
      },
      (err: unknown) => {
        // A chunk that 404s after a deploy is the realistic case. Say so, and
        // offer the way out (Try again) right where the player is looking.
        if (!live) return;
        console.error('[meta] the meta chunk failed to load', err);
        const p = options.root.ownerDocument.createElement('p');
        p.className = 'p-6 text-sm text-text-dim';
        p.setAttribute('role', 'alert');
        p.dataset.testid = 'meta-chunk-failed';
        p.textContent = t('meta.chunk_failed');
        const retry = createButton({
          label: t('common.retry'),
          icon: 'action-retry',
          variant: 'primary',
          class: 'mx-6',
          testId: 'meta-chunk-retry',
          onPress: () => {
            retry.destroy();
            attempt();
          },
        });
        options.root.replaceChildren(p, retry.root);
      },
    );
  };
  attempt();

  return () => {
    live = false;
    inner?.();
    inner = null;
    // A screen's teardown is not required to empty the node it was handed.
    options.root.replaceChildren();
  };
}

/* ═══════════════════════════════════════════════ leaving a live match ══ */

/**
 * THE EXIT GUARD — one registration, every way out.
 *
 * ── THE HOLE THIS CLOSES ────────────────────────────────────────────────────
 * A player in a live match reaches the meta shell's chrome: the left rail, the
 * phone tab bar, the header, and the browser's own back button. Before this,
 * every one of those was an unconditional `navigate()` — pressing Cards during
 * a match tore the socket down on the way out, the server saw a disconnect
 * rather than a surrender, and the opponent waited out a reconnect window for
 * an opponent who had gone to read card text. Nothing was confirmed and nothing
 * was forfeited.
 *
 * A guard bolted onto the nav rail would only cover the rail. This one lives
 * where EVERY navigation already funnels — `main.ts#navigate` for taps and
 * links, `popstate` for back/forward — which is why it is a router concern and
 * why it is in the shell file rather than in the match chunk that arms it.
 *
 * ── WHY THE REGISTRY IS HERE AND THE DIALOG IS NOT ──────────────────────────
 * This file is shell (ADR-0005, 150 KB gz). What is here is three references
 * and two functions with no DOM, no i18n and no imports. The confirmation
 * itself — a modal, eight translations, a forfeit — is `engine/modes/
 * leave-match-confirm.ts`, in the meta chunk, reached by a dynamic import at
 * the moment a guarded navigation is actually attempted.
 *
 * ── THE ARMER IS THE SESSION ────────────────────────────────────────────────
 * `engine/match/net/session.ts` arms this the moment its phase becomes
 * `NET_PLAYING` and disarms on `match_end`, on a fatal error and in `destroy()`.
 * That is deliberate: "there is a live match to forfeit" is a fact only the
 * session holds, and a screen that armed it on mount would keep guarding a
 * match that had already ended.
 */
export interface MatchExitGuard {
  /**
   * Ask the player. `proceed()` performs the navigation that was blocked and
   * must be called at most once; not calling it is "stay", which is the whole
   * of what cancel does. The guard is ALREADY disarmed when `proceed` runs, so
   * the resumed navigation cannot re-enter this guard.
   */
  confirm(to: string, proceed: () => void): void;
}

let exitGuard: MatchExitGuard | null = null;
/** Where the guarded surface lives, captured at arm time. Back restores it. */
let guardedPath = '';
/** True while a confirmation is on screen — a second prompt would stack. */
let confirming = false;

/** Arm the guard. Returns its own disarm, so a caller need not keep the ref. */
export function armMatchExitGuard(guard: MatchExitGuard): () => void {
  exitGuard = guard;
  guardedPath = typeof location === 'undefined' ? '' : location.pathname;
  confirming = false;
  return () => {
    if (exitGuard === guard) disarmMatchExitGuard();
  };
}

/** Disarm. Idempotent — `destroy()` and `match_end` both call it. */
export function disarmMatchExitGuard(): void {
  exitGuard = null;
  guardedPath = '';
  confirming = false;
}

/** Is a live match currently guarding the exits? */
export function isMatchExitGuarded(): boolean {
  return exitGuard !== null;
}

/**
 * MAY THE ROUTER GO TO `to`?
 *
 * `true` means "navigate now" and is the answer whenever nothing is armed —
 * which is every navigation outside a live match, so the cost of the guard on
 * the ordinary path is one null check.
 *
 * `false` means the navigation has been TAKEN OVER, not cancelled: the guard
 * asks, and on a yes it calls `resume(to)` itself. `resume` is passed rather
 * than closed over so this file needs no reference to `main.ts`.
 */
export function allowExit(to: string, resume: (to: string) => void): boolean {
  const guard = exitGuard;
  if (guard === null) return true;
  // Navigating to where we already are is not leaving. `mountArenaInShell`
  // renders the arena under `/play`, so both spellings stay.
  if (normalise(to) === normalise(guardedPath)) return true;
  // A second press while the dialog is up must not open a second dialog.
  if (confirming) return false;
  confirming = true;
  guard.confirm(to, () => {
    disarmMatchExitGuard();
    resume(to);
  });
  return false;
}

/**
 * WRAP THE `popstate` LISTENER. The back button is a navigation the router does
 * not get asked about — by the time the event fires the URL has ALREADY moved,
 * so a guard can only ask afterwards and put the URL back if the answer is no.
 *
 * `history.pushState` (not `back()`) is what restores it: calling `back()` here
 * would fire another `popstate` and, on a player who has held the button down,
 * walk the stack. Pushing the guarded path costs one entry and leaves the URL
 * agreeing with what is on screen, which is the invariant that matters.
 */
export function guardPopState(render: () => void): () => void {
  return () => {
    const guard = exitGuard;
    if (guard === null) {
      render();
      return;
    }
    const to = location.pathname + location.search;
    if (normalise(location.pathname) === normalise(guardedPath)) {
      // Back landed on the match itself (our own restore, or a forward). Let
      // the router draw it; nothing is being left.
      render();
      return;
    }
    history.pushState(null, '', guardedPath);
    if (confirming) return;
    confirming = true;
    guard.confirm(to, () => {
      disarmMatchExitGuard();
      history.pushState(null, '', to);
      render();
    });
  };
}

/* ────────────────────────────── how the guard learns a match is live ── */

/**
 * THE TWO EVENT NAMES `engine/match/net/session.ts` DISPATCHES ON `globalThis`.
 *
 * They are duplicated as string literals there rather than imported, and that
 * is deliberate, not sloppy: `session.ts` is imported by the BACKEND's
 * `two-client-e2e.spec.ts`, whose vitest config aliases `@rga/sim` and
 * `@rga/proto` AND NOTHING ELSE — a single `@/engine/...` import in that file
 * resolves under Vite and fails under the backend runner. Its own docblock says
 * so. An event name is the widest seam that costs neither side an import.
 *
 * `test/match-exit.test.ts` asserts the two spellings still agree, which is the
 * price of the duplication and is cheaper than the coupling.
 */
export const MATCH_LIVE_EVENT = 'rga:match-live';
export const MATCH_OVER_EVENT = 'rga:match-over';

/** What `MATCH_LIVE_EVENT` carries: the one verb the confirmation needs. */
export interface MatchLiveDetail {
  surrender(): void;
}

/**
 * SUBSCRIBE THE ROUTER TO LIVE MATCHES, at module load.
 *
 * Two listeners and no work until one fires. This is what makes the guard
 * REACHABLE with a single line in `main.ts` instead of a wiring change in
 * `engine/bootstrap.ts`: the session announces that a match is live, the router
 * hears it, and the exits are guarded from that moment until `match_end`, a
 * fatal error, or the session being destroyed.
 *
 * The confirmation dialog is imported HERE, lazily, so the shell chunk carries
 * the registry and not the modal (ADR-0005).
 */
function subscribeToLiveMatches(): void {
  if (typeof globalThis.addEventListener !== 'function') return;
  globalThis.addEventListener(MATCH_LIVE_EVENT, (event: Event) => {
    const detail = (event as CustomEvent<MatchLiveDetail>).detail;
    if (detail === null || typeof detail?.surrender !== 'function') return;
    armMatchExitGuard({
      confirm(_to, proceed) {
        void import('@/engine/modes/leave-match-confirm').then(
          (mod) => {
            mod.confirmLeaveMatch({
              onConfirm: () => {
                // SURRENDER FIRST, THEN NAVIGATE. The other order tears the
                // socket down before the frame is written and the server sees
                // a disconnect — a reconnect window, not a forfeit.
                detail.surrender();
                proceed();
              },
              onCancel: () => {
                confirming = false;
              },
            });
          },
          (err: unknown) => {
            // The modal chunk is gone (a deploy mid-match). Do NOT strand the
            // player in a match they asked to leave: forfeit and go, which is
            // the answer they were about to be asked for.
            console.error('[route] the leave-match dialog failed to load', err);
            detail.surrender();
            proceed();
          },
        );
      },
    });
  });
  globalThis.addEventListener(MATCH_OVER_EVENT, () => {
    disarmMatchExitGuard();
  });
}

subscribeToLiveMatches();
